import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { AgentComparison } from '@rma/public/compare-agent/private-api/da-compare-agent';
import { SkeletonGridComponent } from '@rma/public/generic/ui/skeleton';
import { StaticAssetUrlPipe } from '@rma/public/generic/util/environment';
import { ProfileBadgeImage } from '@rma/public/profiles/public/domain';
import { COMPARE_AGENT_SCOPE } from '../domain/translation-scope';
import { ComparisonTableComponent } from './comparison-table.component';
import { ComparisonTableCellComponent } from './comparison-table-cell.component';
import { ComparisonTableRowComponent } from './comparison-table-row.component';

export type OverallInformationDetails = Pick<
  AgentComparison,
  | 'agentCode'
  | 'agentProfileUrl'
  | 'agentProfileReviewsUrl'
  | 'agencyName'
  | 'agencyProfileUrl'
  | 'overallStars'
  | 'reviewCount'
  | 'languages'
  | 'sentiments'
  | 'achievements'
>;

const LANGUAGE_SCOPE = 'language';

export const MAX_SENTIMENT_PILLS = 3;

export const getReviewsUrl = (agent: Pick<OverallInformationDetails, 'agentProfileUrl' | 'agentProfileReviewsUrl'>): string =>
  agent.agentProfileReviewsUrl ?? `${agent.agentProfileUrl}/reviews`;

@Component({
  selector: 'rma-overall-information-section',
  templateUrl: './overall-information-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope(COMPARE_AGENT_SCOPE), provideTranslocoScope(LANGUAGE_SCOPE)],
  imports: [
    ComparisonTableCellComponent,
    ComparisonTableComponent,
    ComparisonTableRowComponent,
    DecimalPipe,
    MatTooltipModule,
    RmaIconComponent,
    RouterLink,
    SkeletonGridComponent,
    StaticAssetUrlPipe,
    TranslocoDirective,
  ],
  host: {
    class: 'block',
  },
})
export class OverallInformationSectionComponent {
  protected readonly scope = `${COMPARE_AGENT_SCOPE}.overallInformation`;
  protected readonly languageScope = LANGUAGE_SCOPE;
  protected readonly maxSentimentPills = MAX_SENTIMENT_PILLS;
  protected readonly badgeImage = ProfileBadgeImage;
  protected readonly reviewsUrl = getReviewsUrl;
  protected readonly showSentiments = computed(() => this.agents().some((agent) => !!agent.sentiments?.length));
  protected readonly showLanguages = computed(() => this.agents().some((agent) => !!agent.languages?.length));
  protected readonly showAchievements = computed(() => this.agents().some((agent) => !!agent.achievements?.length));

  readonly agents = input.required<readonly OverallInformationDetails[]>();
}
